import { z } from 'zod';

import type {
  ConversationDetail,
  ConvertConversationRequest,
} from '@nucleus-commerce/api-client/admin';

export const TICKET_PRIORITIES = ['1', '2', '3', '4'] as const;

/** "Convert to ticket": the subject the emails carry, a priority, and an email when none is known. */
export const convertTicketFormSchema = z.object({
  subject: z
    .string()
    .trim()
    .min(1, { error: 'conversations:convert.validation.subjectRequired' })
    .max(200, { error: 'conversations:convert.validation.subjectLength' }),
  priority: z.enum(TICKET_PRIORITIES),
  /** Empty keeps the conversation's own email (or none). */
  email: z
    .string()
    .trim()
    .max(320, { error: 'conversations:convert.validation.email' })
    .refine((value) => value === '' || z.email().safeParse(value).success, {
      error: 'conversations:convert.validation.email',
    }),
});

export type ConvertTicketForm = z.infer<typeof convertTicketFormSchema>;

/** Where ticket replies would go today: the conversation's email, else the visitor's. */
export const knownEmailOf = (conversation: Pick<ConversationDetail, 'email' | 'visitor'>) =>
  conversation.email ?? conversation.visitor?.email ?? null;

export function convertTicketDefaults(
  conversation: Pick<ConversationDetail, 'subject' | 'priority' | 'lastMessage'>,
): ConvertTicketForm {
  const priority = String(conversation.priority);
  return {
    subject: conversation.subject ?? conversation.lastMessage?.preview.slice(0, 200) ?? '',
    priority: (TICKET_PRIORITIES as readonly string[]).includes(priority)
      ? (priority as ConvertTicketForm['priority'])
      : '3',
    email: '',
  };
}

export const toConvertConversation = (values: ConvertTicketForm): ConvertConversationRequest => ({
  kind: 'ticket',
  subject: values.subject,
  priority: Number(values.priority),
  ...(values.email ? { email: values.email } : {}),
});
